import * as React from 'react';

import * as TogglePrimitive from '@radix-ui/react-toggle';
import { tv, type VariantProps } from 'tailwind-variants';

import CancelRoundedIcon from '@mui/icons-material/CancelRounded';

import cn from '../../utils/cn';

const iconVariants = tv({
  base: '',
  variants: {
    color: {
      primary: 'data-[state=on]:border-primary data-[state=on]:bg-primary-lightness data-[state=on]:text-primary-dark',
      secondary: 'data-[state=on]:border-secondary data-[state=on]:bg-secondary-lightness data-[state=on]:text-secondary-dark',
      danger: 'data-[state=on]:border-danger data-[state=on]:bg-danger-lightness data-[state=on]:text-danger-dark',
      warning: 'data-[state=on]:border-warning data-[state=on]:bg-warning-lightness data-[state=on]:text-warning-dark',
      success: 'data-[state=on]:border-success data-[state=on]:bg-success-lightness data-[state=on]:text-success-dark',
      accessory2: 'data-[state=on]:border-accessory-2 data-[state=on]:bg-accessory-2/10 data-[state=on]:text-accessory-2',
      accessory5: 'data-[state=on]:border-accessory-5 data-[state=on]:bg-accessory-5/10 data-[state=on]:text-accessory-5',
    },
  },
});

const chipVariants = tv({
  base: 'flex items-center g-white justify-center rounded-2xl border border-gray-light bg-white px-3.5 text-xs font-medium text-secondary-dark transition-colors duration-200 hover:bg-white active:scale-95 active:ring-0 active:transition-all disabled:active:scale-100',
  variants: {
    variant: {
      default: 'bg-white',
      outlined: 'border-gray-light bg-transparent hover:bg-transparent',
    },
    hover: {
      primary: 'hover:border-primary-dark hover:text-primary-dark',
      secondary: 'hover:border-secondary-dark hover:text-secondary-dark',
      danger: 'hover:border-danger-dark hover:text-danger-dark',
      warning: 'hover:border-warning-dark hover:text-warning-dark',
      success: 'hover:border-success-dark hover:text-success-dark',
      accessory2: 'hover:border-accessory-2 hover:text-accessory-2',
      accessory5: 'hover:border-accessory-5 hover:text-accessory-5',
    },
    color: {
      primary: 'data-[state=on]:border-primary-dark data-[state=on]:bg-primary-lightness data-[state=on]:text-primary-dark',
      secondary: 'data-[state=on]:border-secondary-dark data-[state=on]:bg-secondary-lightness data-[state=on]:text-secondary-dark',
      danger: 'data-[state=on]:border-danger-dark data-[state=on]:bg-danger-lightness data-[state=on]:text-danger-dark',
      warning: 'data-[state=on]:border-warning-dark data-[state=on]:bg-warning-lightness data-[state=on]:text-warning-dark',
      success: 'data-[state=on]:border-success-dark data-[state=on]:bg-success-lightness data-[state=on]:text-success-dark',
      accessory2: 'data-[state=on]:border-accessory-2 data-[state=on]:bg-accessory-2/10 data-[state=on]:text-accessory-2',
      accessory5: 'data-[state=on]:border-accessory-5 data-[state=on]:bg-accessory-5/10 data-[state=on]:text-accessory-5',
    },
    size: {
      regular: 'h-8 py-1',
      condensed: 'h-6 py-0.5',
    },
    disabled: {
      true: 'cursor-not-allowed pointer-events-none bg-secondary-light border-secondary opacity-50 text-gray',
      false: '',
    },
  },
  defaultVariants: {
    hover: 'primary',
    color: 'primary',
    variant: 'default',
    size: 'regular',
    disabled: false,
  },
});

const removeIconVariant = tv({
  base: '',
  variants: {
    size: {
      regular: 'size-5',
      condensed: 'size-4',
    },
    hover: {
      default: 'hover:text-primary-dark',
      primary: 'hover:text-primary-dark',
      secondary: 'hover:text-gray-dark',
      danger: 'hover:text-danger-dark',
      warning: 'hover:text-warning-dark',
      success: 'hover:text-success-dark',
      accessory2: 'hover:text-accessory-2',
      accessory5: 'hover:text-accessory-5',
    },
  },
  defaultVariants: {
    size: 'regular',
    hover: 'default',
  },
});

export type TagProps = React.ComponentPropsWithoutRef<typeof TogglePrimitive.Root> &
  VariantProps<typeof chipVariants> & {
    icon?: React.ReactNode;
    counter?: number;
    deleteIcon?: boolean;
    onDeleteClick?: () => void;
  };

const Tag = React.forwardRef<React.ElementRef<typeof TogglePrimitive.Root>, TagProps>(
  ({ className, children, counter, color, variant, icon, size, deleteIcon, onDeleteClick, disabled, ...props }, ref) => {
    const customIcon = (newIcon: React.ReactNode) => {
      if (!newIcon) return null;
      return React.cloneElement(newIcon as React.ReactElement, {
        className: cn((newIcon as React.ReactElement)?.props?.className || 'size-4 mr-1', iconVariants({ color: color })),
      });
    };

    const pressRemoveClick = onDeleteClick
      ? (e: React.MouseEvent) => {
          onDeleteClick();
          e.stopPropagation();
        }
      : undefined;

    return (
      <TogglePrimitive.Root
        ref={ref}
        disabled={disabled}
        className={cn(
          chipVariants({
            color: color,
            hover: color,
            variant: variant,
            size: size,
            disabled: disabled,
          }),
          [className, 'group']
        )}
        {...props}
      >
        {customIcon(icon)}
        {counter !== undefined && (
          <div className={cn('font-semi-bold mr-1 text-black group-hover:text-current group-data-[state=on]:text-current', disabled ? 'text-current' : '')}>{counter}</div>
        )}
        {children}
        {deleteIcon && (
          <CancelRoundedIcon
            onClick={pressRemoveClick}
            data-testid='remove-icon'
            className={cn(
              '-mr-1 m-2 ',
              removeIconVariant({
                hover: color,
                size: size,
              })
            )}
          />
        )}
        {deleteIcon === false && <span data-testid='remove-icon-placeholder' className='-mr-1 ml-1 size-4' />}
      </TogglePrimitive.Root>
    );
  }
);

Tag.displayName = TogglePrimitive.Root.displayName;

export { chipVariants, removeIconVariant };
export default Tag;
